import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router';
import { api, unwrap } from '../../../api/client';
import type { AssetDetail } from '../../../api/types';
import { AssetLink } from '../../../components/assets/AssetLink';
import { Badge, Card, CardHeader, EmptyState, ErrorBox, Loading, Table, Td, Th, Tr } from '../../../components/ui';
import { formatDate } from '../../../lib/format';

function Period({ from, to, current }: { from: string; to: string | null; current: boolean }) {
  return (
    <span className="whitespace-nowrap">
      {formatDate(from)} – {current ? <Badge tone="green">aktuell</Badge> : formatDate(to)}
    </span>
  );
}

export function AssignmentsTab({ asset }: { asset: AssetDetail }) {
  const path = { path: { ref: asset.id } };
  const assignments = useQuery({ queryKey: ['asset', asset.id, 'assignments'], queryFn: () => unwrap(api.GET('/api/assets/{ref}/assignments', { params: path })) });
  const locations = useQuery({ queryKey: ['asset', asset.id, 'locations'], queryFn: () => unwrap(api.GET('/api/assets/{ref}/locations', { params: path })) });
  const relations = useQuery({ queryKey: ['asset', asset.id, 'relationships'], queryFn: () => unwrap(api.GET('/api/assets/{ref}/relationships', { params: path })) });

  return (
    <div className="grid gap-5 lg:grid-cols-2">
      <Card>
        <CardHeader title="Mitarbeiter-Zeiträume" description="Wer hatte das Gerät wann?" />
        {assignments.isPending ? (
          <Loading />
        ) : assignments.error ? (
          <ErrorBox error={assignments.error} className="m-4" />
        ) : assignments.data.data.length === 0 ? (
          <EmptyState title="Nie zugeordnet" />
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>Mitarbeiter</Th>
                <Th>Zeitraum</Th>
                <Th>Notiz</Th>
              </tr>
            </thead>
            <tbody>
              {assignments.data.data.map((p) => (
                <Tr key={p.id}>
                  <Td>
                    {p.employee ? (
                      <Link to={`/employees/${p.employee.short_code}`} className="text-brand-700 hover:underline">
                        {p.employee.short_code} – {p.employee.name}
                      </Link>
                    ) : (
                      '–'
                    )}
                  </Td>
                  <Td>
                    <Period from={p.valid_from} to={p.valid_to} current={p.is_current} />
                  </Td>
                  <Td className="text-xs text-slate-500">{[p.start_note, p.end_note].filter(Boolean).join(' / ') || '–'}</Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>

      <Card>
        <CardHeader title="Standort-Zeiträume" />
        {locations.isPending ? (
          <Loading />
        ) : locations.error ? (
          <ErrorBox error={locations.error} className="m-4" />
        ) : locations.data.data.length === 0 ? (
          <EmptyState title="Kein Standort erfasst" />
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>Standort</Th>
                <Th>Zeitraum</Th>
                <Th>Notiz</Th>
              </tr>
            </thead>
            <tbody>
              {locations.data.data.map((p) => (
                <Tr key={p.id}>
                  <Td>{p.location?.name ?? '–'}</Td>
                  <Td>
                    <Period from={p.valid_from} to={p.valid_to} current={p.is_current} />
                  </Td>
                  <Td className="text-xs text-slate-500">{[p.start_note, p.end_note].filter(Boolean).join(' / ') || '–'}</Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>

      <Card className="lg:col-span-2">
        <CardHeader title="Ein- und Ausbauten" description="In welchem Gerät war dieses Asset – und was war in diesem Gerät eingebaut?" />
        {relations.isPending ? (
          <Loading />
        ) : relations.error ? (
          <ErrorBox error={relations.error} className="m-4" />
        ) : relations.data.data.length === 0 ? (
          <EmptyState title="Keine Ein- oder Ausbauten" />
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>Art</Th>
                <Th>Gerät / Komponente</Th>
                <Th>Zeitraum</Th>
                <Th>Notiz / Grund</Th>
              </tr>
            </thead>
            <tbody>
              {relations.data.data.map((r) => {
                const other = r.role === 'child' ? r.parent : r.child;
                return (
                  <Tr key={r.id}>
                    <Td>{r.role === 'child' ? 'Eingebaut in' : 'Enthielt'}</Td>
                    <Td>
                      <AssetLink asset={other} /> {other?.name}
                    </Td>
                    <Td>
                      <Period from={r.valid_from} to={r.valid_to} current={r.is_current} />
                    </Td>
                    <Td className="text-xs text-slate-500">{[r.install_note, r.removal_reason].filter(Boolean).join(' / ') || '–'}</Td>
                  </Tr>
                );
              })}
            </tbody>
          </Table>
        )}
      </Card>
    </div>
  );
}
